'use client';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PlusIcon, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface InlineURLManagerProps {
  urls: string[];
  onUrlsChange: (urls: string[]) => void;
  placeholder?: string;
  maxUrls?: number;
  className?: string;
  validateUrl?: boolean;
  allowDuplicates?: boolean;
  iconClassName?: string;
  inputClassName?: string;
  showLabel?: boolean;
  label?: string;
}

// URL validation regex
const URL_REGEX =
  /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;

export function InlineURLManager({
  urls,
  onUrlsChange,
  placeholder = 'https://example.com',
  maxUrls = 5,
  className,
  validateUrl = true,
  allowDuplicates = false,
  iconClassName,
  inputClassName,
  showLabel = false,
  label = 'Add links',
}: InlineURLManagerProps) {
  const [showUrlInput, setShowUrlInput] = useState(false);

  const handleAddUrl = () => {
    if (urls.length < maxUrls) {
      onUrlsChange([...urls, '']);
    }
  };

  const handleRemoveUrl = (index: number) => {
    if (urls.length > 1) {
      const newUrls = urls.filter((_, i) => i !== index);
      onUrlsChange(newUrls);
    } else {
      onUrlsChange(['']);
      setShowUrlInput(false);
    }
  };

  const handleUrlChange = (index: number, value: string) => {
    const newUrls = [...urls];
    newUrls[index] = value;
    onUrlsChange(newUrls);
  };

  const toggleUrlInput = () => {
    if (!showUrlInput) {
      // Initialize with one empty URL if none exist
      if (urls.length === 0 || (urls.length === 1 && urls[0] === '')) {
        onUrlsChange(['']);
      }
    }
    setShowUrlInput(!showUrlInput);
  };

  const validateUrlField = (url: string, index: number) => {
    if (url && validateUrl && !URL_REGEX.test(url)) {
      return false;
    }

    if (url && !allowDuplicates) {
      const duplicateIndex = urls.findIndex((u, i) => i !== index && u === url);
      if (duplicateIndex !== -1) {
        return false;
      }
    }

    return true;
  };

  return (
    <div className={cn('flex items-center gap-2 flex-wrap', className)}>
      {/* URL Toggle Button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={toggleUrlInput}
              // A tooltip describes a control, it does not name one — Radix
              // wires it up as aria-describedby, so an icon-only trigger is
              // still announced as just "button" without this.
              aria-label={showUrlInput ? 'Hide links' : label}
              className={cn(
                'h-9 w-9 p-0 rounded-full transition-all duration-200 bg-[#948780] text-[#eae7e0] hover:bg-[#948780] hover:text-[#eae7e0]',
                showUrlInput
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : '',
                iconClassName
              )}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
              >
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{showUrlInput ? 'Hide links' : label}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Expanding URL Inputs */}
      {showUrlInput && (
        <div className="flex items-center gap-2 animate-in slide-in-from-left-2 duration-300 flex-1 min-w-0">
          <div className="flex flex-col gap-2 flex-1">
            {urls.map((url, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  type="url"
                  placeholder={placeholder}
                  value={url}
                  onChange={(e) => handleUrlChange(index, e.target.value)}
                  className={cn(
                    'flex-1 h-9 text-sm',
                    !validateUrlField(url, index) &&
                      url &&
                      'border-destructive',
                    inputClassName
                  )}
                />
                {urls.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveUrl(index)}
                    className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Add More URL Button */}
          {urls.length < maxUrls && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleAddUrl}
                    className="h-9 w-9 p-0 rounded-full hover:bg-muted transition-all duration-200"
                    aria-label="Add another link"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Add another link</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}

      {/* Optional Label */}
      {showLabel && !showUrlInput && (
        <span className="text-sm text-muted-foreground">{label}</span>
      )}
    </div>
  );
}

export default InlineURLManager;
